import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import SubSegmentedTabs from "@/components/admin/SubSegmentedTabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
    Select, SelectTrigger, SelectContent, SelectValue, SelectItem,
} from "@/components/ui/select";
import {
    Accordion, AccordionItem, AccordionTrigger, AccordionContent,
} from "@/components/ui/accordion";
import { toast } from "sonner";
import { confirmDialog } from "@/lib/confirm";
import {
    Plus, PencilSimple, Trash, Tag, ChartBar, CheckCircle, X,
    Hash, CurrencyInr, Stack, Sparkle, ArrowsClockwise, Calendar,
    ListBullets, Lightning, ClipboardText,
    Cube, HeartStraight, ScanSmiley, Crown,
    Copy, CaretDown,
} from "@phosphor-icons/react";
import { FilterSearchBar, FilterChipRow } from "@/components/doctor/FilterBar";

const EMPTY_TIER = {
    name: "",
    aligner_min: "",
    aligner_count: "",
    retainers_included: false,
    included_retainer_count: 1,
    refinement_included: false,
    refinement_count: 1,
    refinement_aligner_count: "",
    lab_charge: "",
    self_plan_discount: "",
    self_plan_discount_type: "flat",
};

/**
 * iter97.50 — generate the auto-bullets the wizard uses as a fallback,
 * given a tier object (could be a saved row or the in-progress form).
 * Used to pre-populate the editable bullet list when admin opens the
 * dialog so the UX makes it obvious these are editable, not hardcoded.
 *
 * No cap — the wizard renders whatever bullets are returned. Admin can
 * extend the list freely via "Add bullet".
 */
function autoBulletsFor(t) {
    const count = t.aligner_count || "—";
    const bullets = [`Up to ${count} aligners`];
    if (t.refinement_included) {
        const ref = t.refinement_aligner_count;
        bullets.push(ref ? `Refinement up to ${ref}` : "Refinement included");
    } else {
        bullets.push("No refinement");
    }
    bullets.push(t.retainers_included ? "Retainers included" : "No retainers");
    return bullets;
}

const EMPTY_PRIORITY = {
    code: "",
    label: "",
    price_inr: "",
    tat_days: "",
    description: "",
    sort_order: 0,
    card_subtitle: "",
    card_bullets: [],
    card_badge: "",
    card_accent: "primary",
};

const EMPTY_PROMO = {
    name: "",
    code: "",
    discount_type: "percentage",
    discount_value: "",
    applicable_tier_ids: [],
    valid_from: "",
    valid_until: "",
};

function fmtINR(n) {
    if (n == null || Number.isNaN(Number(n))) return "—";
    return `₹${Number(n).toLocaleString("en-IN")}`;
}
function fmtDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
    } catch { return iso; }
}

export default function AdminPricing({ embedded = false, subTab, setSubTab }) {
    const [internalTab, setInternalTab] = useState("tiers");
    // When rendered inside ServiceSetup, the parent owns the sub-tab URL state.
    // When routed directly (legacy), fall back to internal state.
    const tab = subTab != null ? subTab : internalTab;
    const setTab = setSubTab || setInternalTab;
    const [tiers, setTiers] = useState([]);
    const [promos, setPromos] = useState([]);
    const [priorities, setPriorities] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const [t, p, pr] = await Promise.all([
                api.get("/admin/tiers"),
                api.get("/admin/promotions"),
                api.get("/admin/priority-tiers"),
            ]);
            setTiers(t.data);
            setPromos(p.data);
            setPriorities(pr.data);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to load Pricing data");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    return (
        <div data-testid="admin-pricing-page" className="space-y-8">
            {/* Header — hidden when embedded inside Finance hub (the Finance
                page already provides the page title + breadcrumb). */}
            {!embedded && (
                <div>
                    <h1 className="font-display text-3xl font-bold tracking-tight">Pricing</h1>
                    <p className="text-muted-foreground text-sm mt-1">
                        Manage treatment tiers and active discounts. Doctors are billed against the tier their case ships under, with a 30-day invoice window.
                    </p>
                </div>
            )}

            {/* Tabs — canonical SubSegmentedTabs (iter101.35). Uses the
                thin underlined variant so the SECOND-level nesting inside
                Service Setup is visually distinct from the page-level
                pill-filled main-tab bar. */}
            <SubSegmentedTabs
                testid="pricing-tabbar"
                tabTestidBase="tab"
                active={tab}
                onChange={setTab}
                showZero
                tabs={[
                    { id: "tiers",        label: "Treatment Tiers", Icon: ListBullets,   count: tiers.length },
                    { id: "promos",       label: "Promotions",      Icon: Tag,           count: promos.length },
                    { id: "priorities",   label: "Priority Tiers",  Icon: Lightning,     count: priorities.length },
                    { id: "addons",       label: "Add-ons",         Icon: Sparkle },
                    { id: "wizard-cards", label: "Wizard Cards",    Icon: ClipboardText },
                    { id: "wizard-copy",  label: "Wizard Copy",     Icon: PencilSimple },
                ]}
            />

            {tab === "tiers" && <TiersTab tiers={tiers} loading={loading} onReload={load} />}
            {tab === "promos" && <PromosTab promos={promos} tiers={tiers} loading={loading} onReload={load} />}
            {tab === "priorities" && <PrioritiesTab priorities={priorities} loading={loading} onReload={load} />}
            {tab === "addons" && <AddOnsTab />}
            {tab === "wizard-cards" && <PlanningCardsTab />}
            {tab === "wizard-copy" && <WizardCopyTab />}
        </div>
    );
}

/* ──────────── Tiers tab ──────────── */
function TiersTab({ tiers, loading, onReload }) {
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(EMPTY_TIER);
    const [saving, setSaving] = useState(false);

    // iter101.33 — canonical Search + Status chip + Sort grammar for the
    // Treatment Tiers table.
    const [query, setQuery] = useState("");
    const [statusChip, setStatusChip] = useState("all"); // all | active | inactive
    const [sortMode, setSortMode] = useState("range"); // range | name | lab-low | lab-high | recent

    const openCreate = () => {
        setEditing(null);
        // iter97.50 — pre-populate the 3 default bullets so the admin sees
        // editable inputs the moment the dialog opens. They can edit, add,
        // remove, or reorder. The Regenerate button rebuilds these from
        // the current aligner/refinement/retainer settings.
        setForm({ ...EMPTY_TIER });
        setDialogOpen(true);
    };
    const openEdit = (t) => {
        setEditing(t);
        setForm({
            name: t.name,
            aligner_min: t.aligner_min == null ? "" : t.aligner_min,
            aligner_count: t.aligner_count,
            retainers_included: !!t.retainers_included,
            included_retainer_count: t.included_retainer_count ?? 1,
            refinement_included: !!t.refinement_included,
            refinement_count: t.refinement_count ?? 1,
            refinement_aligner_count: t.refinement_aligner_count || "",
            lab_charge: t.lab_charge,
            self_plan_discount: t.self_plan_discount == null ? "" : t.self_plan_discount,
            self_plan_discount_type: t.self_plan_discount_type || "flat",
        });
        setDialogOpen(true);
    };

    // iter101.33 — Duplicate a tier row → pre-populates the create dialog
    // with the source tier's values and "(copy)" suffixed onto the name.
    // Admin can tweak fields (e.g. price) before saving. Uses the existing
    // create endpoint so no backend change is needed.
    const openDuplicate = (t) => {
        setEditing(null); // ensure POST create path, not PUT update
        setForm({
            name: `${t.name} (copy)`,
            aligner_min: t.aligner_min == null ? "" : t.aligner_min,
            aligner_count: t.aligner_count,
            retainers_included: !!t.retainers_included,
            included_retainer_count: t.included_retainer_count ?? 1,
            refinement_included: !!t.refinement_included,
            refinement_count: t.refinement_count ?? 1,
            refinement_aligner_count: t.refinement_aligner_count || "",
            lab_charge: t.lab_charge,
            self_plan_discount: t.self_plan_discount == null ? "" : t.self_plan_discount,
            self_plan_discount_type: t.self_plan_discount_type || "flat",
        });
        setDialogOpen(true);
    };

    const save = async () => {
        const payload = {
            name: (form.name || "").trim(),
            aligner_min: form.aligner_min === "" || form.aligner_min == null ? 0 : (Number(form.aligner_min) || 0),
            aligner_count: Number(form.aligner_count) || 0,
            retainers_included: !!form.retainers_included,
            included_retainer_count: form.retainers_included ? (Number(form.included_retainer_count) || 1) : 0,
            refinement_included: !!form.refinement_included,
            refinement_count: form.refinement_included ? (Number(form.refinement_count) || 1) : 0,
            refinement_aligner_count: form.refinement_included ? Number(form.refinement_aligner_count) || 0 : 0,
            lab_charge: Number(form.lab_charge) || 0,
            self_plan_discount_type: form.self_plan_discount_type || "flat",
            ...(form.self_plan_discount === "" || form.self_plan_discount == null
                ? {}
                : { self_plan_discount: Number(form.self_plan_discount) || 0 }),
        };
        setSaving(true);
        try {
            if (editing) await api.put(`/admin/tiers/${editing.id}`, payload);
            else await api.post("/admin/tiers", payload);
            toast.success(editing ? "Tier updated" : "Tier created");
            setDialogOpen(false);
            await onReload();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to save");
        } finally { setSaving(false); }
    };

    const remove = async (t) => {
        if (!(await confirmDialog({ title: `Deactivate "${t.name}"?`, message: "It will be hidden from new cases. Historical records keep this tier reference.", confirmText: "Deactivate", tone: "danger" }))) return;
        try {
            await api.delete(`/admin/tiers/${t.id}`);
            toast.success("Tier deactivated");
            await onReload();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed");
        }
    };

    // iter101.33 — chip counts + filtered/sorted list. Counts key off the
    // unfiltered `tiers` so admin always sees the true active vs inactive
    // split, not the post-search subset.
    const chipCounts = useMemo(() => ({
        all: tiers.length,
        active: tiers.filter((t) => t.is_active).length,
        inactive: tiers.filter((t) => !t.is_active).length,
    }), [tiers]);

    const filteredTiers = useMemo(() => {
        const q = query.trim().toLowerCase();
        let rows = tiers.filter((t) => {
            if (statusChip === "active" && !t.is_active) return false;
            if (statusChip === "inactive" && t.is_active) return false;
            if (q) {
                const hay = `${t.name || ""} ${t.description || ""}`.toLowerCase();
                if (!hay.includes(q)) return false;
            }
            return true;
        });
        const cmp = (a, b) => {
            switch (sortMode) {
                case "name":     return (a.name || "").localeCompare(b.name || "");
                case "lab-low":  return (a.lab_charge || 0) - (b.lab_charge || 0);
                case "lab-high": return (b.lab_charge || 0) - (a.lab_charge || 0);
                case "recent":   return new Date(b.updated_at || b.created_at || 0) - new Date(a.updated_at || a.created_at || 0);
                case "range":
                default:         return (a.aligner_min || 0) - (b.aligner_min || 0);
            }
        };
        return [...rows].sort(cmp);
    }, [tiers, query, statusChip, sortMode]);

    return (
        <>
            <div className="flex items-center justify-between mb-4">
                <div>
                    <h2 className="font-display text-lg font-semibold">Treatment Tiers</h2>
                    <p className="text-xs text-muted-foreground mt-0.5">The catalogue that gets billed when aligners ship.</p>
                </div>
                <Button onClick={openCreate} className="rounded-full h-10 px-4" data-testid="new-tier-btn">
                    <Plus size={14} className="mr-1.5" weight="bold" /> New tier
                </Button>
            </div>

            {/* iter101.33 — canonical Search + Sort */}
            <div className="space-y-2 mb-3">
                <FilterSearchBar
                    query={query}
                    setQuery={setQuery}
                    placeholder="Search tiers by name…"
                    testid="tiers-filter-bar"
                    searchTestid="tiers-search-input"
                    rightSlot={
                        <div className="relative">
                            <select
                                value={sortMode}
                                onChange={(e) => setSortMode(e.target.value)}
                                data-testid="tiers-sort-select"
                                className="appearance-none h-9 pl-3 pr-8 rounded-full bg-secondary/40 border border-border text-[12px] font-medium text-foreground focus:bg-card focus:outline-none"
                            >
                                <option value="range">Sort · Aligner range</option>
                                <option value="name">Name A–Z</option>
                                <option value="lab-low">Lab charge · low → high</option>
                                <option value="lab-high">Lab charge · high → low</option>
                                <option value="recent">Recently updated</option>
                            </select>
                            <CaretDown size={11} weight="bold" className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                        </div>
                    }
                />
                <FilterChipRow
                    testid="tiers-chip-row"
                    chipTestidPrefix="tiers-chip"
                    value={statusChip}
                    onChange={setStatusChip}
                    chips={[
                        { id: "all",      label: "All",      count: chipCounts.all,      tone: "slate" },
                        { id: "active",   label: "Active",   count: chipCounts.active,   tone: "emerald" },
                        { id: "inactive", label: "Inactive", count: chipCounts.inactive, tone: "slate" },
                    ]}
                />
            </div>

            <div className="bg-card border border-border rounded-2xl overflow-hidden">
                <div className="hidden md:grid grid-cols-[1.4fr_0.9fr_0.9fr_1.1fr_0.9fr_0.9fr_110px] gap-3 px-5 py-3 bg-secondary/40 border-b border-border text-[11px] font-bold tracking-[0.15em] uppercase text-foreground/70">
                    <div>Tier</div>
                    <div>Stages</div>
                    <div>Retainers</div>
                    <div>Refinement</div>
                    <div>Lab charge</div>
                    <div>Status</div>
                    <div />
                </div>

                {loading && <div className="p-10 text-center text-sm text-muted-foreground">Loading…</div>}
                {!loading && tiers.length === 0 && (
                    <div className="p-12 text-center" data-testid="tiers-empty">
                        <Stack size={32} weight="duotone" className="mx-auto mb-2 text-muted-foreground opacity-40" />
                        <p className="text-sm text-muted-foreground mb-4">No tiers yet.</p>
                        <Button onClick={openCreate} size="sm" className="rounded-full">
                            <Plus size={14} className="mr-1" /> Create first tier
                        </Button>
                    </div>
                )}
                {!loading && tiers.length > 0 && filteredTiers.length === 0 && (
                    <div className="p-10 text-center" data-testid="tiers-filtered-empty">
                        <Stack size={28} weight="duotone" className="mx-auto mb-2 text-muted-foreground opacity-40" />
                        <p className="text-sm text-muted-foreground">No tiers match this filter.</p>
                        <button
                            type="button"
                            onClick={() => { setQuery(""); setStatusChip("all"); }}
                            className="text-xs text-primary hover:underline mt-1"
                            data-testid="tiers-filter-clear"
                        >
                            Clear filters
                        </button>
                    </div>
                )}

                <div className="divide-y divide-border" data-testid="tiers-rows">
                    {filteredTiers.map((t) => (
                        <div
                            key={t.id}
                            className="grid grid-cols-1 md:grid-cols-[1.4fr_0.9fr_0.9fr_1.1fr_0.9fr_0.9fr_110px] gap-3 px-5 py-4 items-center hover:bg-secondary/30 transition-colors"
                            data-testid={`tier-row-${t.id}`}
                        >
                            <div className="min-w-0">
                                <div className="font-semibold flex items-center gap-2">
                                    <Sparkle size={13} weight="fill" className="text-primary" />
                                    {t.name}
                                </div>
                                {t.description && <div className="text-xs text-muted-foreground mt-0.5 truncate">{t.description}</div>}
                            </div>
                            <div className="text-sm">
                                {/* iter98.99da — Display tier range in STAGES
                                    (industry-standard clinical progression unit). */}
                                {t.aligner_count > 0 ? (
                                    t.aligner_min > 0
                                        ? <><span className="font-semibold tabular-nums">{t.aligner_min}&ndash;{t.aligner_count}</span> stages</>
                                        : <>Up to <span className="font-semibold tabular-nums">{t.aligner_count}</span> stages</>
                                ) : <span className="text-muted-foreground italic">Not set</span>}
                                {t.complexity_label && (
                                    <div className="text-[10px] text-muted-foreground mt-0.5">{t.complexity_label}</div>
                                )}
                            </div>
                            <div>
                                {t.retainers_included
                                    ? <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-primary"><CheckCircle size={12} weight="fill" /> Yes</span>
                                    : <span className="text-[11px] text-muted-foreground">No</span>}
                            </div>
                            <div className="text-sm">
                                {t.refinement_included
                                    ? <span className="inline-flex items-center gap-1 text-primary font-semibold"><CheckCircle size={12} weight="fill" /> {t.refinement_count || 1} × up to {t.refinement_aligner_count} stages</span>
                                    : <span className="text-[11px] text-muted-foreground">No</span>}
                            </div>
                            <div className="font-semibold tabular-nums">
                                {t.lab_charge > 0 ? fmtINR(t.lab_charge) : <span className="text-muted-foreground italic font-normal">Not set</span>}
                                {/* iter98.99au — self-plan discount preview (supports flat ₹ + percent) */}
                                {t.self_plan_discount != null && t.self_plan_discount > 0 && (() => {
                                    const isPct = (t.self_plan_discount_type || "flat") === "percent";
                                    const eff = isPct
                                        ? Math.max(0, (t.lab_charge || 0) * (1 - t.self_plan_discount / 100))
                                        : Math.max(0, (t.lab_charge || 0) - t.self_plan_discount);
                                    const off = isPct
                                        ? `${t.self_plan_discount}%`
                                        : fmtINR(t.self_plan_discount);
                                    return (
                                        <div className="text-[10px] font-normal text-emerald-700 mt-0.5">
                                            Self-plan: <span className="font-mono tabular-nums">{fmtINR(eff)}</span>
                                            <span className="text-muted-foreground"> (−{off})</span>
                                        </div>
                                    );
                                })()}
                            </div>
                            <div>
                                <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${
                                    t.is_active
                                        ? "bg-primary/10 text-primary border-primary/20"
                                        : "bg-foreground/5 text-foreground/60 border-foreground/10"
                                }`}>
                                    {t.is_active ? "Active" : "Inactive"}
                                </span>
                            </div>
                            <div className="flex items-center gap-1 justify-end">
                                <Button variant="ghost" size="icon" onClick={() => openEdit(t)} title="Edit" data-testid={`tier-edit-${t.id}`}>
                                    <PencilSimple size={14} />
                                </Button>
                                <Button variant="ghost" size="icon" onClick={() => openDuplicate(t)} title="Duplicate" data-testid={`tier-duplicate-${t.id}`}>
                                    <Copy size={14} />
                                </Button>
                                {t.is_active && (
                                    <Button variant="ghost" size="icon" onClick={() => remove(t)} className="text-rose-600 hover:bg-rose-50" title="Deactivate" data-testid={`tier-delete-${t.id}`}>
                                        <Trash size={14} />
                                    </Button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Tier create/edit dialog */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col p-0 gap-0">
                    <DialogHeader className="px-6 pt-6 pb-3 border-b border-border shrink-0">
                        <DialogTitle>{editing ? `Edit ${editing.name}` : "New treatment tier"}</DialogTitle>
                        <DialogDescription>
                            Doctors are billed against the tier their case ships under. Inactive tiers stay on past cases but disappear from new ones.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4 px-6 py-4 overflow-y-auto flex-1 min-h-0">
                        <div>
                            <Label className="text-xs font-semibold">Name</Label>
                            <Input
                                value={form.name}
                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                placeholder="Lite / Standard / Complete"
                                className="h-10 mt-1"
                                data-testid="tier-form-name"
                            />
                        </div>

                        {/* iter98.99da — Renamed "Aligners" → "Stages"
                            in admin tier form so the unit matches the
                            industry-standard clinical progression unit
                            (1 stage = 1 upper + 1 lower aligner set).
                            DB keys (aligner_min, aligner_count) kept
                            for backwards-compat. */}
                        <div className="grid grid-cols-3 gap-3">
                            <div>
                                <Label className="text-xs font-semibold">Min stages</Label>
                                <div className="relative mt-1">
                                    <Hash size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                                    <Input
                                        type="number" min={0}
                                        value={form.aligner_min}
                                        onChange={(e) => setForm({ ...form, aligner_min: e.target.value })}
                                        className="h-10 pl-8"
                                        placeholder="18"
                                        data-testid="tier-form-aligner-min"
                                    />
                                </div>
                            </div>
                            <div>
                                <Label className="text-xs font-semibold">Max stages</Label>
                                <div className="relative mt-1">
                                    <Hash size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                                    <Input
                                        type="number" min={1}
                                        value={form.aligner_count}
                                        onChange={(e) => setForm({ ...form, aligner_count: e.target.value })}
                                        className="h-10 pl-8"
                                        placeholder="24"
                                        data-testid="tier-form-aligner-count"
                                    />
                                </div>
                            </div>
                            <div>
                                <Label className="text-xs font-semibold">Lab charge (₹)</Label>
                                <div className="relative mt-1">
                                    <CurrencyInr size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                                    <Input
                                        type="number" min={0} step={100}
                                        value={form.lab_charge}
                                        onChange={(e) => setForm({ ...form, lab_charge: e.target.value })}
                                        className="h-10 pl-8"
                                        placeholder="25000"
                                        data-testid="tier-form-lab-charge"
                                    />
                                </div>
                                <p className="text-[10px] text-muted-foreground mt-1">Doctor pays this when CoE plans.</p>
                            </div>
                        </div>

                        {/* iter98.99ax — Complexity + Duration removed per
                            user direction: those are clinical decisions made
                            by the doctor on a per-case basis, not tier
                            attributes. The Step 6 tier card now shows the
                            range instead. */}

                        {/* iter98.99au — Self-plan discount supports BOTH flat ₹
                            and percent via the type toggle. Live "Doctor pays"
                            chip recomputes per type so admin sees the effective
                            number. */}
                        <div className="rounded-xl border border-emerald-200 bg-emerald-50/40 p-3">
                            <div className="flex items-baseline justify-between gap-2 mb-2">
                                <div>
                                    <div className="text-xs font-bold tracking-wide text-emerald-900">Self-plan discount (optional)</div>
                                    <div className="text-[10px] text-emerald-900/70">Knocked off the Lab charge when the doctor authors the plan themselves. Pick flat ₹ amount or % of lab charge.</div>
                                </div>
                                {(() => {
                                    const v = Number(form.self_plan_discount) || 0;
                                    const lab = Number(form.lab_charge) || 0;
                                    if (!lab || v <= 0) return null;
                                    const isPct = (form.self_plan_discount_type || "flat") === "percent";
                                    const eff = isPct
                                        ? Math.max(0, lab * (1 - v / 100))
                                        : Math.max(0, lab - v);
                                    return (
                                        <div className="text-right shrink-0">
                                            <div className="text-[10px] text-emerald-900/70 uppercase tracking-wider">Doctor pays</div>
                                            <div className="font-mono tabular-nums font-bold text-emerald-900 text-sm">
                                                ₹{eff.toLocaleString("en-IN", { maximumFractionDigits: 0 })}
                                            </div>
                                        </div>
                                    );
                                })()}
                            </div>
                            <div className="grid grid-cols-[120px_1fr] gap-2">
                                <Select
                                    value={form.self_plan_discount_type || "flat"}
                                    onValueChange={(v) => setForm({ ...form, self_plan_discount_type: v })}
                                >
                                    <SelectTrigger className="h-10" data-testid="tier-form-self-plan-discount-type">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="flat">Flat ₹</SelectItem>
                                        <SelectItem value="percent">Percent %</SelectItem>
                                    </SelectContent>
                                </Select>
                                <div className="relative">
                                    {(form.self_plan_discount_type || "flat") === "percent" ? (
                                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-xs">%</span>
                                    ) : (
                                        <CurrencyInr size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                                    )}
                                    <Input
                                        type="number" min={0} step={(form.self_plan_discount_type || "flat") === "percent" ? 1 : 100}
                                        max={(form.self_plan_discount_type || "flat") === "percent" ? 100 : undefined}
                                        value={form.self_plan_discount}
                                        onChange={(e) => setForm({ ...form, self_plan_discount: e.target.value })}
                                        className={`h-10 ${(form.self_plan_discount_type || "flat") === "percent" ? "pl-3 pr-8" : "pl-8"}`}
                                        placeholder={(form.self_plan_discount_type || "flat") === "percent" ? "15" : "2000"}
                                        data-testid="tier-form-self-plan-discount"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* iter98.99ax — Recommended / Popular badge toggles
                            removed: per user, those belong on the Submission
                            Mode cards (Self-Plan vs CoE), not on the
                            treatment tier itself. The free-text `card_badge`
                            field below is retained for tier-specific
                            marketing chips. */}

                        <div className="flex items-center justify-between p-3 rounded-xl border border-border">
                            <div>
                                <div className="text-sm font-semibold">Refinement included</div>
                                <div className="text-[11px] text-muted-foreground">Allow mid-treatment plan refinement under this tier.</div>
                            </div>
                            <Switch
                                checked={form.refinement_included}
                                onCheckedChange={(v) => setForm({ ...form, refinement_included: v })}
                                data-testid="tier-form-refinement-toggle"
                            />
                        </div>

                        {form.refinement_included && (
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div>
                                    <Label className="text-xs font-semibold">Number of refinements</Label>
                                    <div className="relative mt-1">
                                        <ArrowsClockwise size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                                        <Input
                                            type="number" min={1}
                                            value={form.refinement_count}
                                            onChange={(e) => setForm({ ...form, refinement_count: e.target.value })}
                                            className="h-10 pl-8"
                                            placeholder="2"
                                            data-testid="tier-form-refinement-count"
                                        />
                                    </div>
                                    <p className="text-[10px] text-muted-foreground mt-1">
                                        Free refinements (events). E.g., 2 = doctor gets two refinements at ₹0.
                                    </p>
                                </div>
                                <div>
                                    <Label className="text-xs font-semibold">Max stages per refinement</Label>
                                    <div className="relative mt-1">
                                        <Stack size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                                        <Input
                                            type="number" min={1}
                                            value={form.refinement_aligner_count}
                                            onChange={(e) => setForm({ ...form, refinement_aligner_count: e.target.value })}
                                            className="h-10 pl-8"
                                            placeholder="8"
                                            data-testid="tier-form-refinement-aligner-cap"
                                        />
                                    </div>
                                    <p className="text-[10px] text-muted-foreground mt-1">
                                        Soft cap. Extra stages bill at the per-stage overage rate.
                                    </p>
                                </div>
                            </div>
                        )}

                        <div className="flex items-center justify-between p-3 rounded-xl border border-border">
                            <div>
                                <div className="text-sm font-semibold">Retainers included</div>
                                <div className="text-[11px] text-muted-foreground">Bundle final retainers with this tier.</div>
                            </div>
                            <Switch
                                checked={form.retainers_included}
                                onCheckedChange={(v) => setForm({ ...form, retainers_included: v })}
                                data-testid="tier-form-retainers-toggle"
                            />
                        </div>

                        {form.retainers_included && (
                            <div>
                                <Label className="text-xs font-semibold">Retainers included (count)</Label>
                                <div className="relative mt-1">
                                    <Stack size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                                    <Input
                                        type="number" min={1}
                                        value={form.included_retainer_count}
                                        onChange={(e) => setForm({ ...form, included_retainer_count: e.target.value })}
                                        className="h-10 pl-8"
                                        placeholder="1"
                                        data-testid="tier-form-retainer-count"
                                    />
                                </div>
                                <p className="text-[10px] text-muted-foreground mt-1">
                                    Doctors can order this many retainer cases without extra charge. Beyond this, doctors are billed the standard lab fee.
                                </p>
                            </div>
                        )}

                        {/* iter98.99ay — Wizard card appearance + Sort order
                            blocks removed per user audit. The wizard now
                            auto-renders tier cards from the structural
                            fields (name + range + refinement + retainer +
                            price). Default ordering is by aligner_min ASC. */}
                    </div>

                    <DialogFooter className="px-6 py-4 border-t border-border bg-secondary/20 shrink-0">
                        <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
                        <Button onClick={save} disabled={saving} data-testid="tier-form-save">
                            {saving ? "Saving…" : (editing ? "Save changes" : "Create tier")}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}

/* ──────────── Promotions tab ──────────── */
function PromosTab({ promos, tiers, loading, onReload }) {
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(EMPTY_PROMO);
    const [saving, setSaving] = useState(false);

    const activeTiers = useMemo(() => tiers.filter((t) => t.is_active), [tiers]);

    const openCreate = () => { setEditing(null); setForm(EMPTY_PROMO); setDialogOpen(true); };
    const openEdit = (p) => {
        setEditing(p);
        setForm({
            name: p.name,
            code: p.code,
            discount_type: p.discount_type,
            discount_value: p.discount_value,
            applicable_tier_ids: p.applicable_tier_ids || [],
            valid_from: p.valid_from || "",
            valid_until: p.valid_until || "",
        });
        setDialogOpen(true);
    };

    const toggleTier = (tierId) => {
        setForm((f) => ({
            ...f,
            applicable_tier_ids: f.applicable_tier_ids.includes(tierId)
                ? f.applicable_tier_ids.filter((id) => id !== tierId)
                : [...f.applicable_tier_ids, tierId],
        }));
    };

    const save = async () => {
        const payload = {
            name: (form.name || "").trim(),
            code: (form.code || "").trim().toUpperCase(),
            discount_type: form.discount_type,
            discount_value: Number(form.discount_value) || 0,
            applicable_tier_ids: form.applicable_tier_ids || [],
            valid_from: form.valid_from,
            valid_until: form.valid_until,
        };
        setSaving(true);
        try {
            if (editing) await api.put(`/admin/promotions/${editing.id}`, payload);
            else await api.post("/admin/promotions", payload);
            toast.success(editing ? "Promotion updated" : "Promotion created");
            setDialogOpen(false);
            await onReload();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to save");
        } finally { setSaving(false); }
    };

    const remove = async (p) => {
        if (!(await confirmDialog({ title: `Deactivate "${p.code}"?`, message: "Patients with this code will no longer redeem it.", confirmText: "Deactivate", tone: "danger" }))) return;
        try {
            await api.delete(`/admin/promotions/${p.id}`);
            toast.success("Promotion deactivated");
            await onReload();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed");
        }
    };

    return (
        <>
            <div className="flex items-center justify-between mb-4">
                <div>
                    <h2 className="font-display text-lg font-semibold">Promotions</h2>
                    <p className="text-xs text-muted-foreground mt-0.5">Discounts applied at quote time. Code-based, scoped to specific tiers if needed.</p>
                </div>
                <Button onClick={openCreate} className="rounded-full h-10 px-4" data-testid="new-promo-btn">
                    <Plus size={14} className="mr-1.5" weight="bold" /> New promotion
                </Button>
            </div>

            <div className="bg-card border border-border rounded-2xl overflow-hidden">
                <div className="hidden md:grid grid-cols-[1.2fr_1.3fr_1fr_1fr_1.2fr_0.9fr_70px] gap-3 px-5 py-3 bg-secondary/40 border-b border-border text-[11px] font-bold tracking-[0.15em] uppercase text-foreground/70">
                    <div>Code</div>
                    <div>Name</div>
                    <div>Discount</div>
                    <div>Applies to</div>
                    <div>Validity</div>
                    <div>Status</div>
                    <div />
                </div>

                {loading && <div className="p-10 text-center text-sm text-muted-foreground">Loading…</div>}
                {!loading && promos.length === 0 && (
                    <div className="p-12 text-center" data-testid="promos-empty">
                        <Tag size={32} weight="duotone" className="mx-auto mb-2 text-muted-foreground opacity-40" />
                        <p className="text-sm text-muted-foreground mb-4">No promotions yet.</p>
                        <Button onClick={openCreate} size="sm" className="rounded-full">
                            <Plus size={14} className="mr-1" /> Create first promotion
                        </Button>
                    </div>
                )}

                <div className="divide-y divide-border" data-testid="promos-rows">
                    {promos.map((p) => {
                        const tierNames = (p.applicable_tier_ids || [])
                            .map((id) => tiers.find((t) => t.id === id)?.name)
                            .filter(Boolean);
                        return (
                            <div
                                key={p.id}
                                className="grid grid-cols-1 md:grid-cols-[1.2fr_1.3fr_1fr_1fr_1.2fr_0.9fr_70px] gap-3 px-5 py-4 items-center hover:bg-secondary/30 transition-colors"
                                data-testid={`promo-row-${p.id}`}
                            >
                                <div className="font-mono text-sm font-bold text-primary truncate">{p.code}</div>
                                <div className="text-sm truncate">{p.name}</div>
                                <div className="font-semibold tabular-nums">
                                    {p.discount_type === "percentage" ? `${p.discount_value}%` : fmtINR(p.discount_value)}
                                </div>
                                <div className="text-xs">
                                    {tierNames.length === 0
                                        ? <span className="text-muted-foreground italic">All tiers</span>
                                        : tierNames.join(", ")}
                                </div>
                                <div className="text-xs flex items-center gap-1.5">
                                    <Calendar size={12} weight="duotone" className="text-muted-foreground" />
                                    <span>{fmtDate(p.valid_from)} → {fmtDate(p.valid_until)}</span>
                                </div>
                                <div>
                                    <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${
                                        p.is_active
                                            ? "bg-primary/10 text-primary border-primary/20"
                                            : "bg-foreground/5 text-foreground/60 border-foreground/10"
                                    }`}>
                                        {p.is_active ? "Active" : "Inactive"}
                                    </span>
                                </div>
                                <div className="flex items-center gap-1 justify-end">
                                    <Button variant="ghost" size="icon" onClick={() => openEdit(p)} data-testid={`promo-edit-${p.id}`}>
                                        <PencilSimple size={14} />
                                    </Button>
                                    {p.is_active && (
                                        <Button variant="ghost" size="icon" onClick={() => remove(p)} className="text-rose-600 hover:bg-rose-50" data-testid={`promo-delete-${p.id}`}>
                                            <Trash size={14} />
                                        </Button>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Promo create/edit dialog */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col p-0 gap-0">
                    <DialogHeader className="px-6 pt-6 pb-3 border-b border-border shrink-0">
                        <DialogTitle>{editing ? `Edit ${editing.code}` : "New promotion"}</DialogTitle>
                        <DialogDescription>
                            Discount codes applied at the time of quoting. Codes are uppercase, unique, and validity is enforced by date.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4 px-6 py-4 overflow-y-auto flex-1 min-h-0">
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="text-xs font-semibold">Code</Label>
                                <Input
                                    value={form.code}
                                    onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                                    placeholder="WELCOME10"
                                    className="h-10 mt-1 font-mono uppercase tracking-wider"
                                    data-testid="promo-form-code"
                                />
                            </div>
                            <div>
                                <Label className="text-xs font-semibold">Name (internal)</Label>
                                <Input
                                    value={form.name}
                                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                                    placeholder="Welcome offer"
                                    className="h-10 mt-1"
                                    data-testid="promo-form-name"
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="text-xs font-semibold">Type</Label>
                                <Select value={form.discount_type} onValueChange={(v) => setForm({ ...form, discount_type: v })}>
                                    <SelectTrigger className="h-10 mt-1" data-testid="promo-form-type">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="percentage">Percentage (%)</SelectItem>
                                        <SelectItem value="flat">Flat amount (₹)</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="text-xs font-semibold">Value</Label>
                                <div className="relative mt-1">
                                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-xs">
                                        {form.discount_type === "percentage" ? "%" : "₹"}
                                    </span>
                                    <Input
                                        type="number" min={0.01} step={0.01}
                                        value={form.discount_value}
                                        onChange={(e) => setForm({ ...form, discount_value: e.target.value })}
                                        className="h-10 pl-8"
                                        placeholder={form.discount_type === "percentage" ? "10" : "5000"}
                                        data-testid="promo-form-value"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="text-xs font-semibold">Valid from</Label>
                                <Input
                                    type="date"
                                    value={form.valid_from}
                                    onChange={(e) => setForm({ ...form, valid_from: e.target.value })}
                                    className="h-10 mt-1"
                                    data-testid="promo-form-valid-from"
                                />
                            </div>
                            <div>
                                <Label className="text-xs font-semibold">Valid until</Label>
                                <Input
                                    type="date"
                                    value={form.valid_until}
                                    onChange={(e) => setForm({ ...form, valid_until: e.target.value })}
                                    className="h-10 mt-1"
                                    data-testid="promo-form-valid-until"
                                />
                            </div>
                        </div>

                        <div>
                            <Label className="text-xs font-semibold">Applicable tiers</Label>
                            <p className="text-[11px] text-muted-foreground mb-2 mt-0.5">Leave all unchecked to apply to every tier.</p>
                            <div className="flex flex-wrap gap-2">
                                {activeTiers.length === 0 && (
                                    <p className="text-xs text-muted-foreground italic">No active tiers. Create one in the Tiers tab first.</p>
                                )}
                                {activeTiers.map((t) => {
                                    const checked = form.applicable_tier_ids.includes(t.id);
                                    return (
                                        <button
                                            key={t.id}
                                            type="button"
                                            onClick={() => toggleTier(t.id)}
                                            data-testid={`promo-tier-chip-${t.id}`}
                                            className={`inline-flex items-center gap-1.5 px-3 h-8 rounded-full text-sm font-semibold border transition-colors ${
                                                checked
                                                    ? "bg-primary/10 text-primary border-primary/30"
                                                    : "bg-card text-foreground border-border hover:border-primary/40"
                                            }`}
                                        >
                                            {checked ? <CheckCircle size={12} weight="fill" /> : <Plus size={12} weight="bold" />}
                                            {t.name}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    </div>

                    <DialogFooter className="px-6 py-4 border-t border-border bg-secondary/20 shrink-0">
                        <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
                        <Button onClick={save} disabled={saving} data-testid="promo-form-save">
                            {saving ? "Saving…" : (editing ? "Save changes" : "Create promotion")}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}



/* ──────────── Priority Tiers tab ────────────
 * Lets admin manage the paid speed-up options doctors see on the case
 * wizard. Standard is the implicit free baseline and is NOT stored —
 * only Rush / STAT (or custom faster tiers admin may add later) live
 * in this catalog. Soft-delete via the standard `is_active=false` flag
 * preserves historical case references.
 */
function PrioritiesTab({ priorities, loading, onReload }) {
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(EMPTY_PRIORITY);
    const [saving, setSaving] = useState(false);

    const openCreate = () => { setEditing(null); setForm(EMPTY_PRIORITY); setDialogOpen(true); };
    const openEdit = (p) => {
        setEditing(p);
        setForm({
            code: p.code,
            label: p.label,
            price_inr: p.price_inr,
            tat_days: p.tat_days,
            description: p.description || "",
            sort_order: p.sort_order || 0,
            card_subtitle: p.card_subtitle || "",
            card_bullets: Array.isArray(p.card_bullets) ? p.card_bullets : [],
            card_badge: p.card_badge || "",
            card_accent: p.card_accent || "primary",
        });
        setDialogOpen(true);
    };

    const save = async () => {
        const payload = {
            code: (form.code || "").trim().toLowerCase(),
            label: (form.label || "").trim(),
            price_inr: Number(form.price_inr) || 0,
            tat_days: Number(form.tat_days) || 0,
            description: form.description || "",
            sort_order: Number(form.sort_order) || 0,
            card_subtitle: (form.card_subtitle || "").trim(),
            card_bullets: (form.card_bullets || []).map((b) => (b || "").trim()).filter(Boolean),
            card_badge: (form.card_badge || "").trim(),
            card_accent: form.card_accent || "primary",
        };
        if (!payload.code) { toast.error("Code is required (e.g. rush, stat)"); return; }
        if (!payload.label) { toast.error("Label is required"); return; }
        if (payload.tat_days <= 0) { toast.error("TAT days must be positive"); return; }
        setSaving(true);
        try {
            if (editing) await api.put(`/admin/priority-tiers/${editing.id}`, payload);
            else await api.post("/admin/priority-tiers", payload);
            toast.success(editing ? "Priority tier updated" : "Priority tier created");
            setDialogOpen(false);
            await onReload();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to save");
        } finally { setSaving(false); }
    };

    const remove = async (p) => {
        if (!(await confirmDialog({ title: `Deactivate "${p.label}"?`, message: "Doctors will no longer see this priority option.", confirmText: "Deactivate", tone: "danger" }))) return;
        try {
            await api.delete(`/admin/priority-tiers/${p.id}`);
            toast.success("Priority tier deactivated");
            await onReload();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed");
        }
    };

    return (
        <>
            <div className="flex items-center justify-between mb-4">
                <div>
                    <h2 className="font-display text-lg font-bold">Priority pricing</h2>
                    <p className="text-xs text-muted-foreground mt-0.5">
                        Manufacturing speed options shown to doctors at case submission.
                    </p>
                </div>
                <Button onClick={openCreate} size="sm" className="rounded-full" data-testid="priority-create-btn">
                    <Plus size={14} weight="bold" className="mr-1" /> New tier
                </Button>
            </div>

            <div className="border border-border rounded-2xl overflow-hidden bg-card" data-testid="priorities-table">
                <div className="grid grid-cols-[1fr_1fr_1fr_1fr_2fr_auto] gap-4 px-5 py-3 bg-secondary/40 border-b border-border text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                    <div>Code</div>
                    <div>Label</div>
                    <div className="text-right">Add-on fee</div>
                    <div className="text-right">TAT</div>
                    <div>Description</div>
                    <div className="w-12 text-right">Actions</div>
                </div>
                {loading ? (
                    <div className="px-5 py-12 text-center text-sm text-muted-foreground">Loading…</div>
                ) : priorities.length === 0 ? (
                    <div className="px-5 py-12 text-center text-sm text-muted-foreground">
                        No priority tiers yet. Add Rush + STAT to give doctors faster turnaround options.
                    </div>
                ) : (
                    priorities.map((p) => (
                        <div
                            key={p.id}
                            className={`grid grid-cols-[1fr_1fr_1fr_1fr_2fr_auto] gap-4 px-5 py-4 border-b border-border last:border-b-0 items-center text-sm ${p.is_active ? "" : "opacity-50"}`}
                            data-testid={`priority-row-${p.code}`}
                        >
                            <div className="font-mono text-xs font-semibold uppercase">{p.code}</div>
                            <div className="font-semibold flex items-center gap-1.5">
                                <Lightning
                                    size={12}
                                    weight="duotone"
                                    className={p.code === "stat" ? "text-amber-500" : p.code === "rush" ? "text-blue-500" : "text-emerald-500"}
                                />
                                {p.label}
                            </div>
                            <div className="text-right font-display font-bold tabular-nums">
                                {Number(p.price_inr || 0) > 0 ? `+${fmtINR(p.price_inr)}` : "Free"}
                            </div>
                            <div className="text-right tabular-nums">{p.tat_days}d</div>
                            <div className="text-muted-foreground text-xs">{p.description || "—"}</div>
                            <div className="flex items-center gap-1 justify-end">
                                <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => openEdit(p)} title="Edit" data-testid={`priority-edit-${p.code}`}>
                                    <PencilSimple size={13} />
                                </Button>
                                {p.is_active && (
                                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-rose-600 hover:text-rose-700 hover:bg-rose-50" onClick={() => remove(p)} title="Deactivate" data-testid={`priority-remove-${p.code}`}>
                                        <Trash size={13} />
                                    </Button>
                                )}
                            </div>
                        </div>
                    ))
                )}
            </div>

            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col p-0 gap-0" data-testid="priority-form-dialog">
                    <DialogHeader className="px-6 pt-6 pb-3 border-b border-border shrink-0">
                        <DialogTitle>{editing ? "Edit priority tier" : "New priority tier"}</DialogTitle>
                        <DialogDescription>
                            Speed-up option doctors can pick at case submission. Codes are lowercase and unique (e.g. <code>rush</code>, <code>stat</code>).
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 px-6 py-4 overflow-y-auto flex-1 min-h-0">
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="text-xs font-semibold">Code</Label>
                                <Input
                                    value={form.code}
                                    onChange={(e) => setForm({ ...form, code: e.target.value.toLowerCase() })}
                                    placeholder="rush"
                                    className="h-10 mt-1 font-mono"
                                    disabled={!!editing}
                                    data-testid="priority-form-code"
                                />
                            </div>
                            <div>
                                <Label className="text-xs font-semibold">Display label</Label>
                                <Input
                                    value={form.label}
                                    onChange={(e) => setForm({ ...form, label: e.target.value })}
                                    placeholder="Rush"
                                    className="h-10 mt-1"
                                    data-testid="priority-form-label"
                                />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="text-xs font-semibold">Add-on fee (₹)</Label>
                                <div className="relative mt-1">
                                    <CurrencyInr size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                                    <Input
                                        type="number" min={0}
                                        value={form.price_inr}
                                        onChange={(e) => setForm({ ...form, price_inr: e.target.value })}
                                        placeholder="3000"
                                        className="h-10 pl-8"
                                        data-testid="priority-form-price"
                                    />
                                </div>
                            </div>
                            <div>
                                <Label className="text-xs font-semibold">TAT (days)</Label>
                                <div className="relative mt-1">
                                    <Hash size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                                    <Input
                                        type="number" min={0.5} step="0.5"
                                        value={form.tat_days}
                                        onChange={(e) => setForm({ ...form, tat_days: e.target.value })}
                                        placeholder="5"
                                        className="h-10 pl-8"
                                        data-testid="priority-form-tat"
                                    />
                                </div>
                            </div>
                        </div>
                        <div>
                            <Label className="text-xs font-semibold">Description (optional)</Label>
                            <Textarea
                                rows={2}
                                value={form.description}
                                onChange={(e) => setForm({ ...form, description: e.target.value })}
                                placeholder="Shown to doctors under the option."
                                className="mt-1"
                                data-testid="priority-form-description"
                            />
                        </div>
                        <div>
                            <Label className="text-xs font-semibold">Sort order</Label>
                            <Input
                                type="number"
                                value={form.sort_order}
                                onChange={(e) => setForm({ ...form, sort_order: e.target.value })}
                                placeholder="10"
                                className="h-10 mt-1"
                                data-testid="priority-form-sort"
                            />
                            <p className="text-[10px] text-muted-foreground mt-1">
                                Lower numbers appear first. Standard is 0; Rush typically 10; STAT 20.
                            </p>
                        </div>

                        <div className="border-t border-dashed border-border pt-4 mt-2 space-y-3">
                            <div className="flex items-center gap-2">
                                <ClipboardText size={14} className="text-primary" weight="duotone" />
                                <Label className="text-sm font-bold">Wizard card appearance</Label>
                                <span className="text-[10px] text-muted-foreground font-mono ml-auto">All optional</span>
                            </div>
                            <p className="text-[11px] text-muted-foreground -mt-1">
                                Customise how this manufacturing speed appears on the doctor's wizard card. Empty fields fall back to label + ₹fee + TAT-day defaults.
                            </p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div>
                                    <Label className="text-xs font-semibold">Card subtitle</Label>
                                    <Input
                                        value={form.card_subtitle}
                                        onChange={(e) => setForm({ ...form, card_subtitle: e.target.value })}
                                        className="h-9 mt-1"
                                        placeholder="When time matters"
                                        data-testid="priority-form-card-subtitle"
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs font-semibold">Card badge</Label>
                                    <Input
                                        value={form.card_badge}
                                        onChange={(e) => setForm({ ...form, card_badge: e.target.value })}
                                        className="h-9 mt-1"
                                        placeholder="FASTEST"
                                        data-testid="priority-form-card-badge"
                                    />
                                </div>
                            </div>
                            <div>
                                <Label className="text-xs font-semibold">Card accent</Label>
                                <div className="flex flex-wrap gap-2 mt-1">
                                    {["primary", "slate", "emerald", "amber", "violet"].map((accent) => {
                                        const cls = {
                                            primary: "bg-primary",
                                            slate: "bg-slate-500",
                                            emerald: "bg-emerald-500",
                                            amber: "bg-amber-500",
                                            violet: "bg-violet-500",
                                        }[accent];
                                        const sel = (form.card_accent || "primary") === accent;
                                        return (
                                            <button
                                                key={accent}
                                                type="button"
                                                onClick={() => setForm({ ...form, card_accent: accent })}
                                                className={`inline-flex items-center gap-1.5 px-3 h-8 rounded-full border text-xs font-semibold transition-all ${sel ? "border-foreground ring-2 ring-foreground/15" : "border-border hover:border-foreground/40"}`}
                                                data-testid={`priority-form-accent-${accent}`}
                                            >
                                                <span className={`w-2.5 h-2.5 rounded-full ${cls}`} />
                                                <span className="capitalize">{accent}</span>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                            <div>
                                <div className="flex items-center justify-between">
                                    <Label className="text-xs font-semibold">Custom card bullets ({(form.card_bullets || []).length})</Label>
                                    <Button
                                        type="button"
                                        onClick={() => setForm({ ...form, card_bullets: [...(form.card_bullets || []), ""] })}
                                        variant="ghost"
                                        size="sm"
                                        className="h-7 text-xs"
                                        data-testid="priority-form-add-bullet"
                                    >
                                        <Plus size={12} className="mr-1" /> Add bullet
                                    </Button>
                                </div>
                                <div className="space-y-1.5 mt-1">
                                    {(form.card_bullets || []).map((b, i) => (
                                        <div key={i} className="flex items-center gap-1.5">
                                            <Input
                                                value={b}
                                                onChange={(e) => {
                                                    const next = [...(form.card_bullets || [])];
                                                    next[i] = e.target.value;
                                                    setForm({ ...form, card_bullets: next });
                                                }}
                                                className="h-9 text-sm"
                                                placeholder="Custom bullet"
                                                data-testid={`priority-form-card-bullet-${i}`}
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setForm({ ...form, card_bullets: (form.card_bullets || []).filter((_, idx) => idx !== i) })}
                                                className="p-1.5 text-rose-600 hover:bg-rose-50 rounded"
                                                aria-label="Remove bullet"
                                            >
                                                <Trash size={13} />
                                            </button>
                                        </div>
                                    ))}
                                    {(form.card_bullets || []).length === 0 && (
                                        <p className="text-[10px] text-muted-foreground italic">
                                            Empty — wizard shows price + TAT + description by default.
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                    <DialogFooter className="px-6 py-4 border-t border-border bg-secondary/20 shrink-0">
                        <Button variant="ghost" onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
                        <Button onClick={save} disabled={saving} data-testid="priority-form-save">
                            {saving ? "Saving…" : editing ? "Save changes" : "Create tier"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}


/* ──────────── Wizard Cards tab — admin-editable planning-mode cards ──────────── */

/* iter98.99q — Reusable swatch row used by multiple admin editors to
 * pick an accent / badge color from a fixed palette. The palette must
 * match the Tailwind classes the wizard's tone-map knows about.
 * `allowNone` adds a "Inherit from accent" pill at the start so an
 * empty value means "no override". */
const ACCENT_OPTIONS = [
    { key: "primary", cls: "bg-primary" },
    { key: "slate",   cls: "bg-slate-500" },
    { key: "emerald", cls: "bg-emerald-500" },
    { key: "amber",   cls: "bg-amber-500" },
    { key: "violet",  cls: "bg-violet-500" },
    { key: "rose",    cls: "bg-rose-500" },
];

function ColorSwatchRow({ value, onChange, allowNone = false, testIdPrefix = "swatch" }) {
    return (
        <div className="flex flex-wrap gap-2 mt-1">
            {allowNone && (
                <button
                    type="button"
                    onClick={() => onChange("")}
                    className={`inline-flex items-center gap-1.5 px-3 h-8 rounded-full border text-xs font-semibold transition-all ${(!value) ? "border-foreground ring-2 ring-foreground/15" : "border-border hover:border-foreground/40"}`}
                    data-testid={`${testIdPrefix}-inherit`}
                >
                    <span className="w-2.5 h-2.5 rounded-full bg-gradient-to-r from-slate-300 to-slate-500" />
                    <span>Inherit</span>
                </button>
            )}
            {ACCENT_OPTIONS.map(({ key, cls }) => {
                const sel = value === key;
                return (
                    <button
                        key={key}
                        type="button"
                        onClick={() => onChange(key)}
                        className={`inline-flex items-center gap-1.5 px-3 h-8 rounded-full border text-xs font-semibold transition-all ${sel ? "border-foreground ring-2 ring-foreground/15" : "border-border hover:border-foreground/40"}`}
                        data-testid={`${testIdPrefix}-${key}`}
                    >
                        <span className={`w-2.5 h-2.5 rounded-full ${cls}`} />
                        <span className="capitalize">{key}</span>
                    </button>
                );
            })}
        </div>
    );
}


function PlanningCardsTab() {
    const [coe, setCoe] = useState(null);
    const [doctor, setDoctor] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(null); // 'coe' | 'doctor' | null

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get("/admin/planning-mode-cards");
            setCoe(r.data.coe);
            setDoctor(r.data.doctor);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to load planning cards");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const save = async (mode, data) => {
        setSaving(mode);
        try {
            const payload = {
                eyebrow: (data.eyebrow || "").trim(),
                title: (data.title || "").trim(),
                bullets: (data.bullets || []).map((b) => (b || "").trim()).filter(Boolean),
                best_for: (data.best_for || "").trim(),
                timeline: (data.timeline || "").trim(),
                accent: data.accent || (mode === "doctor" ? "slate" : "primary"),
                subtitle: (data.subtitle || "").trim(),
                badge: (data.badge || "").trim(),
                // iter98.99q — independent badge color override.
                badge_color: (data.badge_color || "").trim(),
            };
            await api.put(`/admin/planning-mode-cards/${mode}`, payload);
            toast.success(`${mode === "coe" ? "Planning service" : "Self-plan"} card saved`);
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Save failed");
        } finally {
            setSaving(null);
        }
    };

    if (loading) {
        return <div className="text-muted-foreground text-sm py-12 text-center">Loading…</div>;
    }
    return (
        <div data-testid="planning-cards-tab" className="space-y-4">
            <div className="rounded-2xl border border-border bg-secondary/30 p-4">
                <div className="text-sm font-semibold">Wizard cards · Step 1 editing surfaces</div>
                <p className="text-[12px] text-muted-foreground mt-1">
                    Every card the doctor sees on Step 1 of the case-submission wizard lives
                    here. Pick a surface below to edit titles, bullets, badges and accent
                    colors. Changes go live on the wizard the instant you save.
                </p>
            </div>
            <Accordion
                type="single"
                collapsible
                className="space-y-3"
                data-testid="wizard-cards-accordion"
            >
                <WizardCardSection
                    value="planning-modes"
                    icon={Crown}
                    accent="emerald"
                    section="§ 02"
                    title="Submission mode cards"
                    helper="CoE Planning vs Self-plan picker (Step 1 § 02)."
                    testid="wizard-card-acc-planning-modes"
                >
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <CardEditor
                            mode="coe"
                            accentClass="border-primary/40 bg-primary/[0.02]"
                            badge="Planning Service"
                            data={coe}
                            setData={setCoe}
                            onSave={() => save("coe", coe)}
                            saving={saving === "coe"}
                        />
                        <CardEditor
                            mode="doctor"
                            accentClass="border-slate-300 bg-slate-50"
                            badge="Self-plan"
                            data={doctor}
                            setData={setDoctor}
                            onSave={() => save("doctor", doctor)}
                            saving={saving === "doctor"}
                        />
                    </div>
                </WizardCardSection>

                <WizardCardSection
                    value="scan-source"
                    icon={Cube}
                    accent="primary"
                    section="§ 01"
                    title="Scan source cards"
                    helper="Self-capture vs IO Scans by Invisique™ (Step 1 § 01)."
                    testid="wizard-card-acc-scan-source"
                >
                    <ScanSourceCardsSection />
                </WizardCardSection>

                <WizardCardSection
                    value="scan-hero"
                    icon={ScanSmiley}
                    accent="amber"
                    section="§ 01"
                    title="IO Scans hero panel"
                    helper="The premium hero that appears after picking IO Scans by Invisique™."
                    testid="wizard-card-acc-scan-hero"
                >
                    <ScanServiceHeroSection />
                </WizardCardSection>

                <WizardCardSection
                    value="compliance"
                    icon={HeartStraight}
                    accent="rose"
                    section="§ 03"
                    title="Compliance Monitoring & Support card"
                    helper="Yes / No compliance opt-in (Step 1 § 03)."
                    testid="wizard-card-acc-compliance"
                >
                    <ComplianceCardSection />
                </WizardCardSection>
            </Accordion>
        </div>
    );
}


/* iter98.99r — Reusable Accordion section used by the Wizard Cards tab.
 * Each section renders as its own collapsible card with an accent
 * icon avatar + helper text in the trigger, so the admin always
 * understands which surface they're editing without expanding it. */
function WizardCardSection({ value, icon: Icon, accent = "primary", section, title, helper, children, testid }) {
    const palette = {
        primary: { ring: "border-primary/20",      icon: "bg-primary/10 text-primary",          chip: "bg-primary/10 text-primary" },
        emerald: { ring: "border-emerald-200",     icon: "bg-emerald-100 text-emerald-700",     chip: "bg-emerald-100 text-emerald-700" },
        amber:   { ring: "border-amber-200",       icon: "bg-amber-100 text-amber-700",         chip: "bg-amber-100 text-amber-700" },
        rose:    { ring: "border-rose-200",        icon: "bg-rose-100 text-rose-700",           chip: "bg-rose-100 text-rose-700" },
    };
    const p = palette[accent] || palette.primary;
    return (
        <AccordionItem
            value={value}
            className={`rounded-2xl border ${p.ring} bg-white shadow-sm overflow-hidden`}
            data-testid={testid}
        >
            <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-secondary/40">
                <div className="flex items-center gap-3 min-w-0 w-full text-left">
                    <span className={`shrink-0 w-9 h-9 rounded-lg grid place-items-center ${p.icon}`}>
                        {Icon ? <Icon size={18} weight="duotone" /> : null}
                    </span>
                    <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                            {section && (
                                <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold tracking-[0.12em] uppercase ${p.chip}`}>
                                    {section}
                                </span>
                            )}
                            <span className="text-[13.5px] font-semibold text-foreground truncate">{title}</span>
                        </div>
                        {helper && (
                            <div className="text-[11.5px] text-muted-foreground mt-0.5 leading-snug">{helper}</div>
                        )}
                    </div>
                </div>
            </AccordionTrigger>
            <AccordionContent className="px-4 pb-4 pt-2 border-t border-border bg-secondary/10">
                <div className="pt-3">
                    {children}
                </div>
            </AccordionContent>
        </AccordionItem>
    );
}


function CardEditor({ mode, accentClass, badge, data, setData, onSave, saving }) {
    if (!data) return null;
    const upd = (k, v) => setData({ ...data, [k]: v });
    const updBullet = (i, v) => {
        const next = [...(data.bullets || [])];
        next[i] = v;
        setData({ ...data, bullets: next });
    };
    const addBullet = () => {
        setData({ ...data, bullets: [...(data.bullets || []), ""] });
    };
    const removeBullet = (i) => {
        const next = (data.bullets || []).filter((_, idx) => idx !== i);
        setData({ ...data, bullets: next });
    };
    return (
        <div
            className={`rounded-2xl border-2 p-5 space-y-4 ${accentClass}`}
            data-testid={`planning-card-editor-${mode}`}
        >
            <div className="flex items-center justify-between">
                <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-card border border-border text-[10px] font-bold uppercase tracking-wider">
                    {badge}
                </div>
                <Button
                    onClick={onSave}
                    disabled={saving}
                    size="sm"
                    data-testid={`planning-card-save-${mode}`}
                >
                    {saving ? "Saving…" : "Save"}
                </Button>
            </div>
            <div className="space-y-3">
                <div>
                    <Label className="text-xs font-semibold">Eyebrow (small uppercase)</Label>
                    <Input
                        value={data.eyebrow || ""}
                        onChange={(e) => upd("eyebrow", e.target.value)}
                        className="h-9 mt-1"
                        placeholder="Planning service"
                        data-testid={`planning-card-${mode}-eyebrow`}
                    />
                </div>
                <div>
                    <Label className="text-xs font-semibold">Title</Label>
                    <Input
                        value={data.title || ""}
                        onChange={(e) => upd("title", e.target.value)}
                        className="h-9 mt-1"
                        placeholder="Use Invisique™'s expert planning"
                        data-testid={`planning-card-${mode}-title`}
                    />
                </div>
                {/* iter92.9 — new subtitle + badge + accent fields */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                        <Label className="text-xs font-semibold">Subtitle (optional)</Label>
                        <Input
                            value={data.subtitle || ""}
                            onChange={(e) => upd("subtitle", e.target.value)}
                            className="h-9 mt-1"
                            placeholder="Best for first-timers"
                            data-testid={`planning-card-${mode}-subtitle`}
                        />
                        <p className="text-[10px] text-muted-foreground mt-1">Caption right under the title.</p>
                    </div>
                    <div>
                        <Label className="text-xs font-semibold">Badge (optional)</Label>
                        <Input
                            value={data.badge || ""}
                            onChange={(e) => upd("badge", e.target.value)}
                            className="h-9 mt-1"
                            placeholder="RECOMMENDED"
                            data-testid={`planning-card-${mode}-badge`}
                        />
                        <p className="text-[10px] text-muted-foreground mt-1">Tiny chip on the top-right.</p>
                    </div>
                </div>
                <div>
                    <Label className="text-xs font-semibold">Card accent</Label>
                    <ColorSwatchRow
                        value={data.accent || "primary"}
                        onChange={(v) => upd("accent", v)}
                        testIdPrefix={`planning-card-${mode}-accent`}
                    />
                </div>
                {/* iter98.99q — Badge color picker. Empty = inherit from accent. */}
                <div>
                    <Label className="text-xs font-semibold">Badge color <span className="font-normal text-muted-foreground italic">· empty inherits the accent</span></Label>
                    <ColorSwatchRow
                        value={data.badge_color || ""}
                        onChange={(v) => upd("badge_color", v)}
                        allowNone
                        testIdPrefix={`planning-card-${mode}-badge-color`}
                    />
                </div>
                <div>
                    <div className="flex items-center justify-between">
                        <Label className="text-xs font-semibold">Bullets ({(data.bullets || []).length})</Label>
                        <Button
                            type="button"
                            onClick={addBullet}
                            variant="ghost"
                            size="sm"
                            className="h-7 text-xs"
                            data-testid={`planning-card-${mode}-add-bullet`}
                        >
                            <Plus size={12} className="mr-1" /> Add bullet
                        </Button>
                    </div>
                    <div className="space-y-1.5 mt-1">
                        {(data.bullets || []).map((b, i) => (
                            <div key={i} className="flex items-center gap-1.5">
                                <Input
                                    value={b}
                                    onChange={(e) => updBullet(i, e.target.value)}
                                    className="h-9 text-sm"
                                    placeholder="Bullet point"
                                    data-testid={`planning-card-${mode}-bullet-${i}`}
                                />
                                <button
                                    type="button"
                                    onClick={() => removeBullet(i)}
                                    className="p-1.5 text-rose-600 hover:bg-rose-50 rounded"
                                    aria-label="Remove bullet"
                                >
                                    <Trash size={13} />
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                    <div>
                        <Label className="text-xs font-semibold">Best for</Label>
                        <Input
                            value={data.best_for || ""}
                            onChange={(e) => upd("best_for", e.target.value)}
                            className="h-9 mt-1"
                            data-testid={`planning-card-${mode}-best-for`}
                        />
                    </div>
                    <div>
                        <Label className="text-xs font-semibold">Timeline</Label>
                        <Input
                            value={data.timeline || ""}
                            onChange={(e) => upd("timeline", e.target.value)}
                            className="h-9 mt-1"
                            data-testid={`planning-card-${mode}-timeline`}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}


/* ──────────── iter98.99q — Scan Source picker cards admin editor ──────────── */
function ScanSourceCardsSection() {
    const [self_capture, setSelf] = useState(null);
    const [io_service, setIO] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get("/admin/scan-source-cards");
            setSelf(r.data.self_capture);
            setIO(r.data.io_service);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to load scan source cards");
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const save = async (mode, data) => {
        setSaving(mode);
        try {
            const payload = {
                eyebrow: (data.eyebrow || "").trim(),
                title: (data.title || "").trim(),
                subtitle: (data.subtitle || "").trim(),
                bullets: (data.bullets || []).map((b) => (b || "").trim()).filter(Boolean),
                badge: (data.badge || "").trim(),
                accent: data.accent || "primary",
                badge_color: (data.badge_color || "").trim(),
            };
            await api.put(`/admin/scan-source-cards/${mode}`, payload);
            toast.success(`${mode === "io_service" ? "IO Scans" : "Self-capture"} card saved`);
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Save failed");
        } finally { setSaving(null); }
    };

    if (loading) return <div className="text-muted-foreground text-sm py-6 text-center">Loading scan-source cards…</div>;
    return (
        <div className="space-y-4" data-testid="scan-source-cards-section">
            <div className="rounded-2xl border border-border bg-secondary/30 p-4">
                <div className="text-sm font-semibold">Scan source picker (Step 1 § 01)</div>
                <p className="text-[12px] text-muted-foreground mt-1">
                    Two cards that appear right after the patient is picked — &quot;Self-capture&quot; vs &quot;IO Scans by Invisique™&quot;. Edit the title, subtitle, bullets, ribbon badge, and accent here.
                </p>
            </div>
            {/* iter98.99v — Wrapped in an emerald outer panel + per-card
                coloured boxes (primary teal · Self-capture; amber · IO
                Scans) so the layout reads identically to the Compliance
                editor below. */}
            <div className="rounded-2xl border-2 border-emerald-300 bg-emerald-50/30 p-5">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                    <SimpleCardEditor
                        testIdPrefix="scan-src-self"
                        label="Self-capture"
                        accent="primary"
                        data={self_capture}
                        setData={setSelf}
                        onSave={() => save("self_capture", self_capture)}
                        saving={saving === "self_capture"}
                    />
                    <SimpleCardEditor
                        testIdPrefix="scan-src-io"
                        label="IO Scans by Invisique™"
                        accent="amber"
                        data={io_service}
                        setData={setIO}
                        onSave={() => save("io_service", io_service)}
                        saving={saving === "io_service"}
                    />
                </div>
            </div>
        </div>
    );
}


/* ──────────── iter98.99q — Generic editor for cards with eyebrow / title / subtitle
   / bullets / badge / accent / badge_color (no Best-for / Timeline rows). Reused by
   ScanSource section and elsewhere. ──────────── */
function SimpleCardEditor({ testIdPrefix, label, accent = "primary", data, setData, onSave, saving }) {
    if (!data) return null;
    const upd = (k, v) => setData({ ...data, [k]: v });
    const updBullet = (i, v) => {
        const next = [...(data.bullets || [])]; next[i] = v;
        setData({ ...data, bullets: next });
    };
    const addBullet = () => setData({ ...data, bullets: [...(data.bullets || []), ""] });
    const removeBullet = (i) => setData({ ...data, bullets: (data.bullets || []).filter((_, idx) => idx !== i) });
    /* iter98.99v — Per-card box styling that matches the Compliance
     * Yes / No grouped boxes (Pricing → Wizard Cards → Compliance).
     * Each Scan-source card now reads as its own coloured surface:
     *   • Self-capture → primary teal eyebrow + border
     *   • IO Scans     → amber eyebrow + border
     * One consistent visual grammar across the whole Wizard Cards tab. */
    const palette = {
        primary: { border: "border-primary/30",   eyebrow: "text-primary" },
        amber:   { border: "border-amber-300",    eyebrow: "text-amber-700" },
        emerald: { border: "border-emerald-200",  eyebrow: "text-emerald-700" },
        rose:    { border: "border-rose-200",     eyebrow: "text-rose-700" },
    };
    const p = palette[accent] || palette.primary;
    return (
        <div className={`rounded-xl border ${p.border} bg-white/70 p-3 space-y-3`} data-testid={`${testIdPrefix}-editor`}>
            <div className="flex items-center justify-between gap-2">
                <div className={`text-[11px] font-bold uppercase tracking-[0.14em] ${p.eyebrow}`}>{label}</div>
                <Button onClick={onSave} size="sm" className="h-8 px-4 rounded-full" disabled={saving} data-testid={`${testIdPrefix}-save`}>
                    {saving ? "Saving…" : "Save"}
                </Button>
            </div>
            <div>
                <Label className="text-xs font-semibold">Eyebrow (optional)</Label>
                <Input value={data.eyebrow || ""} onChange={(e) => upd("eyebrow", e.target.value)} className="h-9 mt-1" placeholder="White-glove records" data-testid={`${testIdPrefix}-eyebrow`} />
            </div>
            <div>
                <Label className="text-xs font-semibold">Title</Label>
                <Input value={data.title || ""} onChange={(e) => upd("title", e.target.value)} className="h-9 mt-1" data-testid={`${testIdPrefix}-title`} />
            </div>
            <div>
                <Label className="text-xs font-semibold">Subtitle (optional)</Label>
                <Input value={data.subtitle || ""} onChange={(e) => upd("subtitle", e.target.value)} className="h-9 mt-1" data-testid={`${testIdPrefix}-subtitle`} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                    <Label className="text-xs font-semibold">Badge text</Label>
                    <Input value={data.badge || ""} onChange={(e) => upd("badge", e.target.value)} className="h-9 mt-1" placeholder="EXCLUSIVE" data-testid={`${testIdPrefix}-badge`} />
                </div>
                <div>
                    <Label className="text-xs font-semibold">Badge color</Label>
                    <ColorSwatchRow value={data.badge_color || ""} onChange={(v) => upd("badge_color", v)} allowNone testIdPrefix={`${testIdPrefix}-badge-color`} />
                </div>
            </div>
            <div>
                <Label className="text-xs font-semibold">Card accent</Label>
                <ColorSwatchRow value={data.accent || "primary"} onChange={(v) => upd("accent", v)} testIdPrefix={`${testIdPrefix}-accent`} />
            </div>
            <div>
                <div className="flex items-center justify-between">
                    <Label className="text-xs font-semibold">Bullets ({(data.bullets || []).length})</Label>
                    <Button type="button" onClick={addBullet} variant="ghost" size="sm" className="h-7 text-xs" data-testid={`${testIdPrefix}-add-bullet`}>
                        <Plus size={12} className="mr-1" /> Add bullet
                    </Button>
                </div>
                <div className="space-y-1.5 mt-1">
                    {(data.bullets || []).map((b, i) => (
                        <div key={i} className="flex items-center gap-1.5">
                            <Input value={b} onChange={(e) => updBullet(i, e.target.value)} className="h-9 text-sm" data-testid={`${testIdPrefix}-bullet-${i}`} />
                            <button type="button" onClick={() => removeBullet(i)} className="p-1.5 text-rose-600 hover:bg-rose-50 rounded" aria-label="Remove bullet">
                                <Trash size={13} />
                            </button>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}


/* ──────────── iter98.99q — Compliance Monitoring & Support card admin editor ──────────── */
function ComplianceCardSection() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get("/admin/compliance-card");
            setData(r.data);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to load compliance card");
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const upd = (k, v) => setData({ ...data, [k]: v });
    const updBullet = (group, i, v) => {
        const next = [...(data[group] || [])]; next[i] = v;
        setData({ ...data, [group]: next });
    };
    const addBullet = (group) => setData({ ...data, [group]: [...(data[group] || []), ""] });
    const removeBullet = (group, i) => setData({ ...data, [group]: (data[group] || []).filter((_, idx) => idx !== i) });

    const save = async () => {
        setSaving(true);
        try {
            const payload = {
                section_eyebrow: (data.section_eyebrow || "").trim(),
                section_title: (data.section_title || "").trim(),
                section_body: (data.section_body || "").trim(),
                yes_title: (data.yes_title || "").trim(),
                no_title: (data.no_title || "").trim(),
                yes_bullets: (data.yes_bullets || []).map((b) => (b || "").trim()).filter(Boolean),
                no_bullets: (data.no_bullets || []).map((b) => (b || "").trim()).filter(Boolean),
                badge: (data.badge || "").trim(),
                badge_color: (data.badge_color || "").trim(),
                no_badge: (data.no_badge || "").trim(),
                no_badge_color: (data.no_badge_color || "").trim(),
            };
            await api.put("/admin/compliance-card", payload);
            toast.success("Compliance card saved");
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Save failed");
        } finally { setSaving(false); }
    };

    if (loading || !data) return <div className="text-muted-foreground text-sm py-6 text-center">Loading compliance card…</div>;
    return (
        <div className="space-y-4" data-testid="compliance-card-section">
            <div className="rounded-2xl border border-border bg-secondary/30 p-4 flex items-start justify-between gap-4">
                <div>
                    <div className="text-sm font-semibold">Compliance Monitoring &amp; Support card (Step 1 § 05)</div>
                    <p className="text-[12px] text-muted-foreground mt-1">
                        Edit the section header copy, the Yes/No option titles and their bullet lists, plus a ribbon badge for each card. Fee is managed separately in the Pricing tab.
                    </p>
                </div>
                <Button onClick={save} size="sm" className="h-8 px-4 rounded-full shrink-0" disabled={saving} data-testid="compliance-card-save">
                    {saving ? "Saving…" : "Save"}
                </Button>
            </div>
            <div className="rounded-2xl border-2 border-emerald-300 bg-emerald-50/30 p-5 space-y-3">
                <div>
                    <Label className="text-xs font-semibold">Section eyebrow</Label>
                    <Input value={data.section_eyebrow || ""} onChange={(e) => upd("section_eyebrow", e.target.value)} className="h-9 mt-1" data-testid="compliance-eyebrow" />
                </div>
                <div>
                    <Label className="text-xs font-semibold">Section title</Label>
                    <Input value={data.section_title || ""} onChange={(e) => upd("section_title", e.target.value)} className="h-9 mt-1" data-testid="compliance-title" />
                </div>
                <div>
                    <Label className="text-xs font-semibold">Section body</Label>
                    <Textarea value={data.section_body || ""} onChange={(e) => upd("section_body", e.target.value)} rows={2} className="mt-1 text-sm" data-testid="compliance-body" />
                </div>
                {/* iter98.99t → iter98.99u — Per-card editor boxes. Each
                    box groups the card's ribbon, its title input and
                    its bullet list into one coloured surface so the
                    admin's eye flows naturally between the Yes and No
                    options. */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                    <div className="rounded-xl border border-emerald-200/80 bg-white/70 p-3 space-y-3">
                        <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-emerald-700">Yes card</div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                                <Label className="text-xs font-semibold">Badge text</Label>
                                <Input value={data.badge || ""} onChange={(e) => upd("badge", e.target.value)} className="h-9 mt-1" placeholder="RECOMMENDED" data-testid="compliance-badge" />
                            </div>
                            <div>
                                <Label className="text-xs font-semibold">Badge color</Label>
                                <ColorSwatchRow value={data.badge_color || ""} onChange={(v) => upd("badge_color", v)} allowNone testIdPrefix="compliance-badge-color" />
                            </div>
                        </div>
                        <BulletGroup
                            title="Bullets"
                            titleField={
                                <Input value={data.yes_title} onChange={(e) => upd("yes_title", e.target.value)} className="h-9 mt-1 text-sm font-semibold" data-testid="compliance-yes-title" />
                            }
                            bullets={data.yes_bullets || []}
                            onAdd={() => addBullet("yes_bullets")}
                            onUpdate={(i, v) => updBullet("yes_bullets", i, v)}
                            onRemove={(i) => removeBullet("yes_bullets", i)}
                            testIdPrefix="compliance-yes"
                        />
                    </div>
                    <div className="rounded-xl border border-rose-200/80 bg-white/70 p-3 space-y-3">
                        <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-rose-700">No card</div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                                <Label className="text-xs font-semibold">Badge text</Label>
                                <Input value={data.no_badge || ""} onChange={(e) => upd("no_badge", e.target.value)} className="h-9 mt-1" placeholder="OPTIONAL" data-testid="compliance-no-badge" />
                            </div>
                            <div>
                                <Label className="text-xs font-semibold">Badge color</Label>
                                <ColorSwatchRow value={data.no_badge_color || ""} onChange={(v) => upd("no_badge_color", v)} allowNone testIdPrefix="compliance-no-badge-color" />
                            </div>
                        </div>
                        <BulletGroup
                            title="Bullets"
                            titleField={
                                <Input value={data.no_title} onChange={(e) => upd("no_title", e.target.value)} className="h-9 mt-1 text-sm font-semibold" data-testid="compliance-no-title" />
                            }
                            bullets={data.no_bullets || []}
                            onAdd={() => addBullet("no_bullets")}
                            onUpdate={(i, v) => updBullet("no_bullets", i, v)}
                            onRemove={(i) => removeBullet("no_bullets", i)}
                            testIdPrefix="compliance-no"
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}


function BulletGroup({ title, titleField, bullets, onAdd, onUpdate, onRemove, testIdPrefix }) {
    return (
        <div className="space-y-2">
            <Label className="text-xs font-semibold">{title}</Label>
            {titleField}
            <div className="space-y-1.5">
                {bullets.map((b, i) => (
                    <div key={i} className="flex items-center gap-1.5">
                        <Input value={b} onChange={(e) => onUpdate(i, e.target.value)} className="h-9 text-sm" data-testid={`${testIdPrefix}-bullet-${i}`} />
                        <button type="button" onClick={() => onRemove(i)} className="p-1.5 text-rose-600 hover:bg-rose-50 rounded">
                            <Trash size={13} />
                        </button>
                    </div>
                ))}
            </div>
            <Button type="button" onClick={onAdd} variant="ghost" size="sm" className="h-7 text-xs" data-testid={`${testIdPrefix}-add`}>
                <Plus size={12} className="mr-1" /> Add bullet
            </Button>
        </div>
    );
}


/* ──────────── iter98.99q — IO Scans hero panel admin editor ──────────── */
function ScanServiceHeroSection() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get("/admin/scan-service-hero");
            setData(r.data);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to load scan service hero");
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const upd = (k, v) => setData({ ...data, [k]: v });
    const updRow = (group, i, field, v) => {
        const next = [...(data[group] || [])];
        next[i] = { ...(next[i] || {}), [field]: v };
        setData({ ...data, [group]: next });
    };
    const addRow = (group, blank) => setData({ ...data, [group]: [...(data[group] || []), blank] });
    const removeRow = (group, i) => setData({ ...data, [group]: (data[group] || []).filter((_, idx) => idx !== i) });

    const save = async () => {
        setSaving(true);
        try {
            const payload = {
                eyebrow: (data.eyebrow || "").trim(),
                title: (data.title || "").trim(),
                subtitle: (data.subtitle || "").trim(),
                promises: (data.promises || []).filter((r) => (r.label || "").trim()),
                features: (data.features || []).filter((r) => (r.label || "").trim()),
                cta_label: (data.cta_label || "").trim(),
                badge: (data.badge || "").trim(),
                badge_color: (data.badge_color || "").trim(),
            };
            await api.put("/admin/scan-service-hero", payload);
            toast.success("Scan service hero saved");
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Save failed");
        } finally { setSaving(false); }
    };

    if (loading || !data) return <div className="text-muted-foreground text-sm py-6 text-center">Loading scan service hero…</div>;
    return (
        <div className="space-y-4" data-testid="scan-service-hero-section">
            <div className="rounded-2xl border border-border bg-secondary/30 p-4 flex items-start justify-between gap-4">
                <div>
                    <div className="text-sm font-semibold">IO Scans hero panel (Step 1 § 01 — after IO Scans is picked)</div>
                    <p className="text-[12px] text-muted-foreground mt-1">
                        The premium hero shown right after the doctor selects &quot;IO Scans by Invisique™&quot;. Edit headline, 3 promise tiles, and 4 feature rows. Icons are referenced by name (Phosphor icon keys).
                    </p>
                </div>
                <Button onClick={save} size="sm" className="h-8 px-4 rounded-full shrink-0" disabled={saving} data-testid="scan-hero-save">
                    {saving ? "Saving…" : "Save"}
                </Button>
            </div>
            <div className="rounded-2xl border-2 border-amber-300 bg-amber-50/30 p-5 space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                        <Label className="text-xs font-semibold">Eyebrow</Label>
                        <Input value={data.eyebrow || ""} onChange={(e) => upd("eyebrow", e.target.value)} className="h-9 mt-1" data-testid="scan-hero-eyebrow" />
                    </div>
                    <div>
                        <Label className="text-xs font-semibold">CTA label</Label>
                        <Input value={data.cta_label || ""} onChange={(e) => upd("cta_label", e.target.value)} className="h-9 mt-1" placeholder="Book scan visit" data-testid="scan-hero-cta" />
                    </div>
                </div>
                <div>
                    <Label className="text-xs font-semibold">Title</Label>
                    <Input value={data.title || ""} onChange={(e) => upd("title", e.target.value)} className="h-9 mt-1" data-testid="scan-hero-title" />
                </div>
                <div>
                    <Label className="text-xs font-semibold">Subtitle</Label>
                    <Textarea value={data.subtitle || ""} onChange={(e) => upd("subtitle", e.target.value)} rows={2} className="mt-1 text-sm" data-testid="scan-hero-subtitle" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                        <Label className="text-xs font-semibold">Hero badge</Label>
                        <Input value={data.badge || ""} onChange={(e) => upd("badge", e.target.value)} className="h-9 mt-1" placeholder="EXCLUSIVE" data-testid="scan-hero-badge" />
                    </div>
                    <div>
                        <Label className="text-xs font-semibold">Badge color</Label>
                        <ColorSwatchRow value={data.badge_color || ""} onChange={(v) => upd("badge_color", v)} allowNone testIdPrefix="scan-hero-badge-color" />
                    </div>
                </div>
                {/* Promises (3 tiles) */}
                <div>
                    <div className="flex items-center justify-between mt-2">
                        <Label className="text-xs font-semibold">Promise tiles ({(data.promises || []).length})</Label>
                        <Button type="button" onClick={() => addRow("promises", { icon: "Sparkle", label: "", sub: "" })} variant="ghost" size="sm" className="h-7 text-xs" data-testid="scan-hero-add-promise">
                            <Plus size={12} className="mr-1" /> Add promise
                        </Button>
                    </div>
                    <div className="space-y-1.5 mt-1">
                        {(data.promises || []).map((row, i) => (
                            <div key={i} className="grid grid-cols-[110px_1fr_1.5fr_auto] gap-1.5 items-center" data-testid={`scan-hero-promise-${i}`}>
                                <Input value={row.icon || ""} onChange={(e) => updRow("promises", i, "icon", e.target.value)} className="h-9 text-xs font-mono" placeholder="Icon key" />
                                <Input value={row.label || ""} onChange={(e) => updRow("promises", i, "label", e.target.value)} className="h-9 text-sm" placeholder="Label" />
                                <Input value={row.sub || ""} onChange={(e) => updRow("promises", i, "sub", e.target.value)} className="h-9 text-sm" placeholder="Sub-copy" />
                                <button type="button" onClick={() => removeRow("promises", i)} className="p-1.5 text-rose-600 hover:bg-rose-50 rounded">
                                    <Trash size={13} />
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
                {/* Features (4 rows) */}
                <div>
                    <div className="flex items-center justify-between mt-2">
                        <Label className="text-xs font-semibold">Feature rows ({(data.features || []).length})</Label>
                        <Button type="button" onClick={() => addRow("features", { icon: "Sparkle", label: "", value: "" })} variant="ghost" size="sm" className="h-7 text-xs" data-testid="scan-hero-add-feature">
                            <Plus size={12} className="mr-1" /> Add feature
                        </Button>
                    </div>
                    <div className="space-y-1.5 mt-1">
                        {(data.features || []).map((row, i) => (
                            <div key={i} className="grid grid-cols-[110px_1fr_1.5fr_auto] gap-1.5 items-center" data-testid={`scan-hero-feature-${i}`}>
                                <Input value={row.icon || ""} onChange={(e) => updRow("features", i, "icon", e.target.value)} className="h-9 text-xs font-mono" placeholder="Icon key" />
                                <Input value={row.label || ""} onChange={(e) => updRow("features", i, "label", e.target.value)} className="h-9 text-sm" placeholder="Label" />
                                <Input value={row.value || ""} onChange={(e) => updRow("features", i, "value", e.target.value)} className="h-9 text-sm" placeholder="Value" />
                                <button type="button" onClick={() => removeRow("features", i)} className="p-1.5 text-rose-600 hover:bg-rose-50 rounded">
                                    <Trash size={13} />
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}


/* ──────────── Wizard Copy tab — admin-editable wizard text snippets ──────────── */
function WizardCopyTab() {
    const [slots, setSlots] = useState([]);
    const [overrides, setOverrides] = useState({});
    const [defaults, setDefaults] = useState({});
    const [drafts, setDrafts] = useState({});    // per-key in-flight edit values
    const [savingKey, setSavingKey] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get("/admin/wizard/copy");
            const _slots = r.data.slots || [];
            setSlots(_slots);
            setOverrides(r.data.overrides || {});
            const def = {};
            _slots.forEach((s) => { def[s.key] = s.default; });
            setDefaults(def);
            // Pre-fill drafts with the current active value so the input
            // shows what the wizard renders (override OR default).
            const initial = {};
            _slots.forEach((s) => {
                initial[s.key] = (r.data.overrides || {})[s.key] || s.default;
            });
            setDrafts(initial);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to load wizard copy");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const save = async (key) => {
        setSavingKey(key);
        try {
            const value = drafts[key] || "";
            const r = await api.put(`/admin/wizard/copy/${key}`, { value });
            if (r.data.cleared) {
                toast.success("Reset to default");
            } else {
                toast.success("Saved");
            }
            await load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Save failed");
        } finally {
            setSavingKey(null);
        }
    };

    const reset = async (key) => {
        setSavingKey(key);
        try {
            await api.put(`/admin/wizard/copy/${key}`, { value: "" });
            toast.success("Reset to default");
            await load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Reset failed");
        } finally {
            setSavingKey(null);
        }
    };

    if (loading) {
        return <div className="text-muted-foreground text-sm py-12 text-center">Loading…</div>;
    }

    // iter92.7 — Group slots by their `group` field so the admin form
    // shows sections instead of one huge flat list as the slot count grows.
    const grouped = slots.reduce((acc, s) => {
        const g = s.group || "Other";
        if (!acc[g]) acc[g] = [];
        acc[g].push(s);
        return acc;
    }, {});
    // Preserve the order groups first appeared in the slot registry —
    // intentional ordering (e.g., Wizard page → Step 1 → Step labels).
    const groupOrder = [];
    slots.forEach((s) => {
        const g = s.group || "Other";
        if (!groupOrder.includes(g)) groupOrder.push(g);
    });

    return (
        <div data-testid="wizard-copy-tab" className="space-y-6">
            <div className="rounded-2xl border border-border bg-secondary/30 p-4">
                <div className="text-sm font-semibold">Wizard text snippets</div>
                <p className="text-[12px] text-muted-foreground mt-1">
                    Override any of these labels to A/B test wizard copy without a deploy.
                    Clear the input or hit <strong>Reset to default</strong> to revert to the
                    hardcoded value. Changes appear on the wizard immediately after save.
                </p>
            </div>
            {groupOrder.map((groupName) => (
                <div key={groupName} className="space-y-3" data-testid={`wizard-copy-group-${groupName.replace(/\s+/g, "-").toLowerCase()}`}>
                    <div className="flex items-center gap-2">
                        <div className="text-[11px] font-bold tracking-[0.18em] uppercase text-muted-foreground">{groupName}</div>
                        <div className="flex-1 h-px bg-border" />
                        <div className="text-[10px] text-muted-foreground tabular-nums">{grouped[groupName].length} slot{grouped[groupName].length === 1 ? "" : "s"}</div>
                    </div>
                    <div className="space-y-3">
                        {grouped[groupName].map((s) => {
                            const overridden = !!(overrides[s.key] || "").trim();
                            const draftVal = drafts[s.key] ?? "";
                            const defaultVal = defaults[s.key] || "";
                            const dirty = (draftVal || "").trim() !== (overridden ? overrides[s.key] : defaultVal);
                            return (
                                <div
                                    key={s.key}
                                    className="rounded-2xl border border-border bg-card p-4 space-y-2.5"
                                    data-testid={`wizard-copy-slot-${s.key}`}
                                >
                                    <div className="flex items-start justify-between gap-3">
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                                <Label className="text-sm font-bold">{s.label}</Label>
                                                {overridden ? (
                                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-[10px] font-bold">
                                                        <CheckCircle size={10} weight="fill" /> Customised
                                                    </span>
                                                ) : (
                                                    <span className="text-[10px] text-muted-foreground font-mono">DEFAULT</span>
                                                )}
                                            </div>
                                            <div className="text-[11px] text-muted-foreground mt-0.5">{s.description}</div>
                                            <div className="text-[10px] text-muted-foreground mt-0.5 font-mono">
                                                key: <span className="text-foreground/80">{s.key}</span>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-1.5 shrink-0">
                                            {overridden && (
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => reset(s.key)}
                                                    disabled={savingKey === s.key}
                                                    data-testid={`wizard-copy-reset-${s.key}`}
                                                >
                                                    Reset
                                                </Button>
                                            )}
                                            <Button
                                                onClick={() => save(s.key)}
                                                disabled={!dirty || savingKey === s.key}
                                                size="sm"
                                                data-testid={`wizard-copy-save-${s.key}`}
                                            >
                                                {savingKey === s.key ? "Saving…" : "Save"}
                                            </Button>
                                        </div>
                                    </div>
                                    <Input
                                        value={draftVal}
                                        onChange={(e) => setDrafts({ ...drafts, [s.key]: e.target.value })}
                                        className="h-10 text-sm"
                                        placeholder={defaultVal}
                                        data-testid={`wizard-copy-input-${s.key}`}
                                    />
                                    {overridden && (
                                        <div className="text-[10px] text-muted-foreground">
                                            <span className="font-semibold">Default:</span> "{defaultVal}"
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            ))}
        </div>
    );
}

/* ──────────── iter101.33 — Add-ons tab ──────────── */
/* Container tab that hosts the two add-on configuration cards (IO Scans
   by Invisique™ + Compliance Monitoring & Support). Both cards were
   previously glued to the top of the Treatment Tiers tab; moving them
   into their own tab keeps Tiers clean and makes the KPI-strip "Add-on
   services" tile a first-class destination. */
function AddOnsTab() {
    return (
        <div className="space-y-4" data-testid="addons-tab">
            <div>
                <h2 className="font-display text-lg font-semibold">Add-on services</h2>
                <p className="text-xs text-muted-foreground mt-0.5">
                    Optional services doctors can opt into during case submission. Fees below are added on top of the treatment tier and snapshotted onto each case so historical billing is immune to future changes.
                </p>
            </div>
            <IoScannerFeeCard />
            <ComplianceFeeCard />
        </div>
    );
}

/* ──────────── iter97.50 — IO Scanning Service fee card ──────────── */
/* Inline card at the top of the Tiers tab. Reads + writes the global
   `io_scanner_pickup_fee` setting. Admin can change anytime; new cases
   snapshot the live value at submit so historical billing is locked in. */
function IoScannerFeeCard() {
    const [fee, setFee] = useState("");
    const [draft, setDraft] = useState("");
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/settings/io-scanner-fee").then((r) => {
            const v = r.data?.fee ?? 3000;
            setFee(v);
            setDraft(String(v));
        }).catch(() => {
            // Treat as default — admin can still save a value.
            setFee(3000);
            setDraft("3000");
        }).finally(() => setLoading(false));
    }, []);

    const dirty = String(fee) !== draft.trim();

    const save = async () => {
        const parsed = Number(draft);
        if (!Number.isFinite(parsed) || parsed < 0) {
            toast.error("Fee must be 0 or a positive number");
            return;
        }
        setSaving(true);
        try {
            const r = await api.put("/admin/settings/io-scanner-fee", { fee: parsed });
            setFee(r.data?.fee ?? parsed);
            setDraft(String(r.data?.fee ?? parsed));
            toast.success("IO Scans by Invisique™ fee saved");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not save the fee");
        }
        setSaving(false);
    };

    return (
        <div className="bg-card border border-border rounded-2xl p-5 mb-6" data-testid="io-scanner-fee-card">
            <div className="flex items-start gap-4">
                <div className="flex-1 min-w-0">
                    <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-primary mb-1">Add-on service</div>
                    <h3 className="font-display text-base font-semibold">IO Scans by Invisique™</h3>
                    <p className="text-xs text-muted-foreground mt-1 max-w-2xl">
                        For doctors without an intra-oral scanner. Our technician visits the clinic and captures the scan. This fee is added on top of the tier price when the doctor opts in on the case wizard.
                    </p>
                </div>
                <div className="shrink-0 w-44">
                    <Label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Fee (₹)</Label>
                    <div className="relative mt-1">
                        <CurrencyInr size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        <Input
                            type="number"
                            min={0}
                            step={100}
                            value={draft}
                            onChange={(e) => setDraft(e.target.value)}
                            disabled={loading}
                            className="h-10 pl-8 font-mono"
                            placeholder="3000"
                            data-testid="io-scanner-fee-input"
                        />
                    </div>
                    {dirty && (
                        <Button
                            onClick={save}
                            disabled={saving}
                            size="sm"
                            className="rounded-full h-8 mt-2 w-full"
                            data-testid="io-scanner-fee-save"
                        >
                            {saving ? "Saving…" : "Save"}
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
}


/* ──────────── iter98.99o — Compliance Monitoring & Support fee card ──────────── */
/* Inline card sits next to IoScannerFeeCard. Reads + writes the global
   `compliance_monitoring_fee` setting. Defaults to ₹0 (offered free for
   now). Wizard Step 1 "Compliance Monitoring & Support" pulls the live
   value so any change here propagates the moment a doctor opens a new
   case. Like the IO scanner fee, this is snapshotted onto the case at
   submit so historical billing is immune to future changes. */
function ComplianceFeeCard() {
    const [fee, setFee] = useState("");
    const [draft, setDraft] = useState("");
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/settings/compliance-fee").then((r) => {
            const v = r.data?.fee ?? 0;
            setFee(v);
            setDraft(String(v));
        }).catch(() => {
            setFee(0);
            setDraft("0");
        }).finally(() => setLoading(false));
    }, []);

    const dirty = String(fee) !== draft.trim();

    const save = async () => {
        const parsed = Number(draft);
        if (!Number.isFinite(parsed) || parsed < 0) {
            toast.error("Fee must be 0 or a positive number");
            return;
        }
        setSaving(true);
        try {
            const r = await api.put("/admin/settings/compliance-fee", { fee: parsed });
            setFee(r.data?.fee ?? parsed);
            setDraft(String(r.data?.fee ?? parsed));
            toast.success(parsed === 0
                ? "Compliance Monitoring & Support is now offered free"
                : "Compliance Monitoring & Support fee saved");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not save the fee");
        }
        setSaving(false);
    };

    return (
        <div className="bg-card border border-border rounded-2xl p-5 mb-6" data-testid="compliance-fee-card">
            <div className="flex items-start gap-4">
                <div className="flex-1 min-w-0">
                    <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-primary mb-1">Add-on service</div>
                    <h3 className="font-display text-base font-semibold">Compliance Monitoring &amp; Support</h3>
                    <p className="text-xs text-muted-foreground mt-1 max-w-2xl">
                        AI-powered weekly photo check-ins, drop-off nudges, and patient-side coaching. Currently offered free; set a per-case fee here if you ever want to monetise the service. The fee surfaces live on the wizard&apos;s Step 1 Compliance card.
                    </p>
                </div>
                <div className="shrink-0 w-44">
                    <Label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Fee (₹)</Label>
                    <div className="relative mt-1">
                        <CurrencyInr size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        <Input
                            type="number"
                            min={0}
                            step={100}
                            value={draft}
                            onChange={(e) => setDraft(e.target.value)}
                            disabled={loading}
                            className="h-10 pl-8 font-mono"
                            placeholder="0"
                            data-testid="compliance-fee-input"
                        />
                    </div>
                    <div className="text-[10.5px] text-muted-foreground mt-1.5 leading-snug">
                        {Number(draft) === 0
                            ? "Free — shown as Included on the case card."
                            : `₹${Number(draft).toLocaleString("en-IN")} per case · added to final invoice.`}
                    </div>
                    {dirty && (
                        <Button
                            onClick={save}
                            disabled={saving}
                            size="sm"
                            className="rounded-full h-8 mt-2 w-full"
                            data-testid="compliance-fee-save"
                        >
                            {saving ? "Saving…" : "Save"}
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
}

